
<template>
    <Section class="add-new">
        <Alert type="error" v-if="error">
            {{ error }}
        </Alert>
        <FormulateForm
            v-model="fields"
            :name="`resource-${this.refLabel}`"
            @submit="addResource"
            ref="form"
        >
            <FormulateInput
                name="title"
                class="add-new--full add-new--title"
                type="text"
                label="Title"
                validation="required"
                error-behavior="submit"
            />

            <FormulateInput
                name="area"
                class="add-new--area"
                type="select"
                label="Area"
                placeholder="Select Area"
                :options="formOptionsAreas"
                error-behavior="submit"
            />

            <FormulateInput
                name="category"
                class="add-new--category"
                type="select"
                label="Category"
                placeholder="Select Category"
                :options="formOptionsCategories"
                error-behavior="submit"
            />

            <FormulateInput
                name="jurisdiction"
                class="add-new--jurisdiction"
                type="text"
                label="Jurisdiction"
                error-behavior="submit"
            />

            <FormulateInput
                name="tags"
                class="add-new--tags"
                type="text"
                label="Tags"
                help="Comma separated"
                validation="required"
                error-behavior="submit"
            />

            <transition name="fade-in" mode="out-in">
                <div class="add-new--group" v-if="!addNewGroup">
                    <FormulateInput
                        name="group"
                        type="select"
                        label="Group"
                        placeholder="Select Group"
                        :options="formOptionsGroups"
                        error-behavior="submit"
                    >
                        <template #label="{ label, id }">
                            <div class="form__label">
                                <label :for="id">{{ label }}</label>
                                <Button display="link" @click="addNewGroup = true">+ Add New</Button>
                            </div>
                        </template>
                    </FormulateInput>
                </div>
            </transition>

            <transition name="fade-in" mode="out-in">
                <div class="add-new--group" v-if="addNewGroup">
                    <FormulateInput
                        name="group"
                        type="text"
                        label="Group"
                        validation="required"
                        placeholder="New Group Name"
                        error-behavior="submit"
                    >
                        <template #label="{ label, id }">
                            <div class="form__label">
                                <label :for="id">{{ label }}</label>
                                <Button display="link" @click="addNewGroup = false">Cancel</Button>
                            </div>
                        </template>
                    </FormulateInput>
                </div>
            </transition>

            <FormulateInput
                name="url"
                class="add-new--full add-new--url"
                type="text"
                :label="label"
                validation="bail|required|video"
                :validation-rules="{
                    video: ({ value }) => validateVideoURL(value)
                }"
                :validation-messages="{
                    video: `The URL is invalid. Double check you're using a correct Vimeo or YouTube URL`
                }"
                error-behavior="submit"
            />

            <footer class="add-new__actions">
                <FormulateInput
                    :class="[
                        { 'btn--loading' : loading },
                        { 'btn--success' : uploaded }
                    ]"
                    type="submit"
                    name="submit"
                >
                    {{ buttonLabel }}
                    <transition name="fade-in" mode="out-in">
                        <Spinner v-if="loading" />
                    </transition>
                </FormulateInput>
            </footer>
        </FormulateForm>
    </Section>
</template>

<script>
import { mapGetters } from 'vuex'
import { parseVideo } from '@/helpers'

export default {
    props: {
        isVideo: { type: Boolean, default: false },
        name: { type: String, required: true }
    },

    data() {
        return {
            fields: {},
            loading: false,
            uploaded: false,
            addNewGroup: false,
            error: null
        }
    },

    watch: {
        fields () {
            this.checkValues()
        }
    },

    mounted () {
        this.$formulate.reset(`resource-${this.refLabel}`)
    },

    computed: {
        ...mapGetters('Resources', ['formOptionsAreas', 'formOptionsGroups', 'formOptionsCategories']),

        refLabel () {
            return this.isVideo ? 'video' : 'external'
        },

        label () {
            return this.isVideo
                ? 'Video URL (YouTube or Vimeo)'
                : 'Resource URL'
        },

        help () {
            return this.isVideo
                ? 'Videos must be hosted on YouTube or Vimeo'
                : 'Link to the external document / website'
        },

        buttonLabel () {
            if (this.loading) return 'Saving Resource'
            if (this.uploaded) return 'Saved Successfully'

            return 'Save Resource'
        }
    },

    methods: {
        checkValues () {
            // This emits controls to prompt the user they're leaving if they have unsaved changes
            const values = Object.values(this.fields).filter(val => val)
            values.length > 0 ? this.$emit('lockTabs') : this.$emit('unlockTabs')

            return values
        },

        validateVideoURL (val) {
            if (!this.isVideo && val) return true

            if (!val) return false

            const videoObj = parseVideo(val)
             if (!videoObj.type || videoObj.id.length < 1) {
                return false
            }

            return true
        },

        addResource () {
            this.loading = true

            const area = this.formOptionsAreas[this.fields.area]
            const category = this.formOptionsCategories[this.fields.category]

            const resource = {
                ...this.fields,
                area,
                category,
                resource_type: this.isVideo ? 'Video' : 'External',
                type: this.isVideo ? 'video' : 'external'
            }

            this.axios.post('/upload-resource', { resource } )
                .then(({ data }) => {
                    if (data.status !== 'success') throw new Error(data.message || "Sorry, we couldn't add your resource. Please try again later.")

                    this.uploaded = true

                    this.fields = {}
                    this.$formulate.reset(`resource-${this.refLabel}`)
                })
                .catch(err => {
                    console.log(err)
                    this.error = err.message || err
                })
                .finally(() => {
                    this.loading = false
                    setTimeout(() => {
                        this.uploaded = false
                    }, 2500);
                })
        }
    }
}
</script>

